import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import {
  Button,
  ButtonColor,
  ButtonSize,
  Checkbox,
  Combobox,
  Modal,
  Option,
  TextArea,
  TextInput,
  ModalType,
} from '@code/innovance-react-ui-kit';

import { ProblemTrackingSystemApi } from 'src/front/api/apiInstance';
import {
  ReferenceDataType,
  PtsComponentsChangeableResponse,
  DocumentPageUploadData,
} from 'src/front/api/generated';

import useYupResolver from 'src/front/common/hooks/useYupResolver';
import usePRM, { useConditionalPRM } from 'src/front/common/hooks/usePRM';
import { PTSNewRecordSchema } from 'src/front/common/utils/validationSchemas';
import { formatDate, convertStringToDate } from 'src/front/common/utils/dateTimeUtils';
import { formatAlphaNumericCyrillicSpecialCharactersWithoutQuotes } from 'src/front/common/utils/formUtils';

import {
  CheckboxContainer,
  Form,
  FormRow,
  FormElement,
  TextAreaContainer,
  Title,
} from 'src/front/components/FormStyles';
import UploadTable from 'src/front/pages/applications/problem-tracking-system/components/UploadTable';

import { useProblemTrackingSystemState } from 'src/front/contexts/ProblemTrackingSystemContext';
import Datepicker from 'src/front/components/Datepicker';

const TEXT_AREA_MAX_LENGTH = 1250;

interface FormValues {
  levelZero?: Option | null;
  levelOne?: Option | null;
  levelTwo?: Option | null;
  levelThree?: Option | null;
  productType?: Option | null;
  channelType?: Option | null;
  sendType?: Option | null;
  statusType?: Option | null;
  closureType?: Option | null;
  deadline?: Date | null;
  SMS?: boolean;
  VIP?: boolean;
  classificationCode?: string | undefined;
  recordDetail?: string | undefined;
}

interface NewRecordFormData {
  levelZero: Option | undefined;
  levelOne: Option | undefined;
  levelTwo: Option | undefined;
  levelThree: Option | undefined;
  productType: Option | undefined;
  channelType: Option | undefined;
  sendType: Option | undefined;
  statusType: Option | undefined;
  closureType: Option | undefined;
  deadline: Date;
  SMS: boolean;
  VIP: boolean;
  classificationCode: string | undefined;
  recordDetail: string | undefined;
}

interface DocumentsUploadData extends DocumentPageUploadData {
  documentType: {
    label?: string;
    code: string;
    value: string;
  };
}

const initialValues = {
  levelZero: null,
  levelOne: null,
  levelTwo: null,
  levelThree: null,
  productType: null,
  channelType: null,
  sendType: null,
  statusType: null,
  closureType: null,
  deadline: null,
  SMS: false,
  VIP: false,
  classificationCode: undefined,
  recordDetail: undefined,
};

const NewRecord: FC = () => {
  const { t } = useTranslation();

  const { customerSearchValues } = useProblemTrackingSystemState();

  const [documentList, setDocumentList] = useState<DocumentsUploadData[]>([]);
  const [isApplicationCompleted, setIsApplicationCompleted] = useState<boolean>(false);
  const [applicationSubmitError, setApplicationSubmitError] = useState<string>();
  const [isSMSEnabled, setIsSMSEnabled] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [changeableComponents, setChangableComponents] = useState<
    PtsComponentsChangeableResponse
  >();

  const resolver = useYupResolver(PTSNewRecordSchema);
  const { formState, register, watch, errors, setValue, handleSubmit, getValues, reset } = useForm<
    FormValues
  >({
    defaultValues: initialValues,
    resolver,
  });

  const levelZeroPRM = usePRM(ReferenceDataType.PTSLEVELZERO);
  const levelOnePRM = usePRM(ReferenceDataType.PTSLEVELONE);
  const levelTwoPRM = useConditionalPRM(
    ReferenceDataType.PTSLEVELTWO,
    watch('levelOne')?.value || ''
  );
  const [levelThreePRM, setLevelThreePRM] = useState<Option[]>([]);
  const productTypePRM = usePRM(ReferenceDataType.PTSPRODUCTTYPE);
  const channelCodePRM = usePRM(ReferenceDataType.PTSCHANNELCODE);
  const sendTypePRM = useConditionalPRM(
    ReferenceDataType.PTSSENDTYPE,
    watch('levelOne')?.value || ''
  );
  const statusTypePRM = usePRM(ReferenceDataType.PTSSTATUS);
  const closureTypePRM = usePRM(ReferenceDataType.PTSCLOSURETYPE);

  useEffect(() => {
    register('levelZero');
    register('levelOne');
    register('levelTwo');
    register('levelThree');
    register('productType');
    register('channelType');
    register('sendType');
    register('statusType');
    register('closureType');
    register({ name: 'deadline' });
    register('SMS');
    register('VIP');
    register('classificationCode');
    register('recordDetail');
    register('levelTwoRequired');
    register('levelThreeRequired');
  }, [register]);

  useEffect(() => {
    setValue('levelTwoRequired', !!levelTwoPRM.length);
    setValue('levelThreeRequired', !!levelThreePRM.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelTwoPRM, levelThreePRM]);

  useEffect(() => {
    // Default Value for Status
    setValue('statusType', statusTypePRM[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTypePRM, refresh]);

  useEffect(() => {
    // Default Value for Send
    setValue('sendType', sendTypePRM[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendTypePRM, refresh]);

  const resetLevelFields = (fieldNames: string[]): void => {
    fieldNames.forEach((field) => {
      setValue(field, null);

      if (field === 'levelThree') setLevelThreePRM([]);
    });
  };

  useEffect(() => {
    (async function getComponents() {
      const { data } = await ProblemTrackingSystemApi.getPtsComponentsChangeable();
      const {
        data: { deadline },
      } = await ProblemTrackingSystemApi.getPtsComponentsValues();

      // Set enable/disable states
      setChangableComponents(data);
      // Set deadline for Today + 30 Days from service
      setValue('deadline', convertStringToDate(deadline));
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const requestLevelThreeCombo = async (): Promise<void> => {
    const levelValues = getValues(['levelOne', 'levelTwo']);

    const { data } = await ProblemTrackingSystemApi.getPtsLevelThreeCombo({
      levelOne: levelValues?.levelOne?.value || '',
      levelTwo: levelValues?.levelTwo?.value || '',
    });

    setLevelThreePRM(
      data.parameterDataList?.map((parameter) => ({
        value: parameter.code,
        label: parameter.value,
      })) || []
    );
  };

  const requestSMS = async (): Promise<void> => {
    const levelValues = getValues(['levelOne', 'levelTwo', 'levelThree', 'channelType']);

    const { data } = await ProblemTrackingSystemApi.getPtsSmsCheckBoxVisibility({
      levelOne: levelValues?.levelOne?.value || '',
      levelTwo: levelValues?.levelTwo?.value || '',
      levelThree: levelValues?.levelThree?.value || '',
      channelCode: levelValues?.channelType?.value || '',
    });

    if (data.isSmsCheckBoxWillBeEnableAndSelect) {
      setValue('SMS', true);
      // Enable SMS Checkbox
      setIsSMSEnabled(true);
    }
  };

  const onComboChange = (fieldName: string) => (value: Option | null): void => {
    setValue(fieldName, value);

    switch (fieldName) {
      case 'levelOne':
        // Level 1 - Reset
        if (value === null) {
          // Reset values for connected fields
          resetLevelFields(['levelTwo', 'levelThree']);
        }

        // Level 1 - Change/Selected (&: Level 0 Set)
        if (value !== null && watch('levelZero')) {
          // If Level 2 is already selected (::change)
          if (watch('levelTwo')) {
            // Reset Level 2 - 3
            resetLevelFields(['levelTwo', 'levelThree']);
            // Disable and un-Require Level 2
          }
        }
        break;
      case 'levelTwo':
        // Level 2 - Reset
        if (value === null) {
          // Reset values for connected fields
          resetLevelFields(['levelThree']);
        }

        // Level 2 - Change/Selected
        if (value !== null) {
          // If Level 3 is already selected (::change)
          if (watch('levelThree')) {
            // Reset Level 3
            setValue('levelThree', null);
          }

          // Request Level 3
          requestLevelThreeCombo();
        }
        break;
      case 'levelThree':
        // Level 2 - Reset
        if (value === null) {
          // Reset SMS
          setValue('SMS', false);
          setIsSMSEnabled(false);
        }

        // Level 3 - Change/Selected
        if (value !== null) {
          // If Level 0, 1, 2 is not empty
          const { levelZero: level0, levelOne: level1, levelTwo: level2 } = getValues();

          if (level0 && level1 && level2) {
            requestSMS();
          }
        }
        break;
      default:
        break;
    }
  };

  const handleDateChange = (fieldName: string) => (date: Date): void => {
    setValue(fieldName, date, {
      shouldValidate: formState.isSubmitted || !!date,
      shouldDirty: formState.isSubmitted || !!date,
    });
  };

  const handleCheckboxChange = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    const { checked, id } = target;

    setValue(id, checked);
  };

  const handleInputChange = (fieldName: string) => (text: string) => {
    setValue(fieldName, text, {
      shouldValidate: formState.isSubmitted || !!text,
      shouldDirty: formState.isSubmitted || !!text,
    });
  };

  const handleTextAreaChange = ({ target }: ChangeEvent<HTMLTextAreaElement>): void => {
    const { value } = target;

    setValue('recordDetail', formatAlphaNumericCyrillicSpecialCharactersWithoutQuotes(value));
  };

  const handleApplicationSuccessClick = (): void => {
    setIsApplicationCompleted(false);

    reset();
    setRefresh(!refresh);
  };

  const handleApplicationErrorClick = (): void => {
    setApplicationSubmitError(undefined);
  };

  const onSubmit = async (formData: NewRecordFormData): Promise<void> => {
    const {
      levelZero,
      levelOne,
      levelTwo,
      levelThree,
      productType,
      channelType,
      sendType,
      statusType,
      closureType,
      recordDetail,
      SMS,
      VIP,
      deadline,
    } = formData;

    const { data } = await ProblemTrackingSystemApi.savePtsRecord({
      levelZero: levelZero?.value || '',
      levelOne: levelOne?.value || '',
      levelTwo: levelTwo?.value || '',
      levelThree: levelThree?.value || '',
      productType: productType?.value || '',
      channelCode: channelType?.value || '',
      sendType: sendType?.value || '',
      status: statusType?.value || '',
      closureType: closureType?.value || '0',
      detailAboutRecord: recordDetail || '',
      isSmsChecked: SMS,
      isVipChecked: VIP,
      deadline: formatDate(deadline),
      documentList,
      ...customerSearchValues,
      birthDate: formatDate(customerSearchValues.birthDate),
    });

    if (data.result === 'OK') setIsApplicationCompleted(true);

    if (data.result === 'ERROR') setApplicationSubmitError(data.message);
  };

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <Title>
            <Trans i18nKey="problemTrackingSystem_dataAboutRecord_text" />
          </Title>
        </FormRow>
        <FormRow>
          <FormElement>
            <Combobox
              errorMessage={t(errors.levelZero?.message as string)}
              items={levelZeroPRM}
              label={t('problemTrackingSystem_level0_label')}
              name="levelZero"
              onChange={onComboChange('levelZero')}
              required={!!levelZeroPRM.length}
              value={watch('levelZero')}
            />
          </FormElement>
          <FormElement>
            <Combobox
              errorMessage={t(errors.levelOne?.message as string)}
              items={levelOnePRM}
              label={t('problemTrackingSystem_level1_label')}
              name="levelOne"
              onChange={onComboChange('levelOne')}
              required={!!levelOnePRM.length}
              value={watch('levelOne')}
            />
          </FormElement>
        </FormRow>
        {watch('levelOne') ? (
          <>
            <FormRow>
              <FormElement>
                <Combobox
                  disabled={!levelTwoPRM.length}
                  errorMessage={t(errors.levelTwo?.message as string)}
                  items={levelTwoPRM}
                  label={t('problemTrackingSystem_level2_label')}
                  name="levelTwo"
                  onChange={onComboChange('levelTwo')}
                  required={!!levelTwoPRM.length}
                  value={watch('levelTwo')}
                />
              </FormElement>
              <FormElement>
                <Combobox
                  disabled={!levelThreePRM.length}
                  errorMessage={t(errors.levelThree?.message as string)}
                  items={levelThreePRM}
                  label={t('problemTrackingSystem_level3_label')}
                  name="levelThree"
                  onChange={onComboChange('levelThree')}
                  required={!!levelThreePRM.length}
                  value={watch('levelThree')}
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <Combobox
                  errorMessage={t(errors.productType?.message as string)}
                  items={productTypePRM}
                  label={t('general_components_productType_label')}
                  name="productType"
                  onChange={onComboChange('productType')}
                  required={watch('levelOne')?.value !== '13'}
                  value={watch('productType')}
                />
              </FormElement>
              <FormElement>
                <Combobox
                  errorMessage={t(errors.channelType?.message as string)}
                  items={channelCodePRM}
                  label={t('general_components_channelCode_label')}
                  name="channelType"
                  onChange={onComboChange('channelType')}
                  value={watch('channelType')}
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <Datepicker
                  disabled={!changeableComponents?.isDeadlineChangeable}
                  errorMessage={t(errors.deadline?.message as string)}
                  inputLabel={`${t('general_components_deadline_label')} *`}
                  onChange={handleDateChange('deadline')}
                  value={watch('deadline') as Date}
                />
              </FormElement>
              <FormElement>
                <CheckboxContainer>
                  <Checkbox
                    disabled={!isSMSEnabled}
                    id="SMS"
                    label={t('problemTrackingSystem_sms_text')}
                    onChange={handleCheckboxChange}
                    value={watch('SMS')}
                  />
                </CheckboxContainer>
                <CheckboxContainer>
                  <Checkbox
                    id="VIP"
                    label={t('problemTrackingSystem_vip_text')}
                    onChange={handleCheckboxChange}
                    value={watch('VIP')}
                  />
                </CheckboxContainer>
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <TextInput
                  disabled={!changeableComponents?.isClassificationCodeChangeable}
                  errorMessage={t(errors.classificationCode?.message as string)}
                  formatter={formatAlphaNumericCyrillicSpecialCharactersWithoutQuotes}
                  label={t('problemTrackingSystem_classificationCode_label')}
                  name="classificationCode"
                  onChange={handleInputChange('classificationCode')}
                  value={watch('classificationCode')}
                />
              </FormElement>
              <FormElement>
                <Combobox
                  errorMessage={t(errors.sendType?.message as string)}
                  items={sendTypePRM}
                  label={t('problemTrackingSystem_send_label')}
                  name="sendType"
                  onChange={onComboChange('sendType')}
                  value={watch('sendType')}
                  disabled
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <Combobox
                  disabled={!changeableComponents?.isStatusChangeable}
                  errorMessage={t(errors.statusType?.message as string)}
                  items={statusTypePRM}
                  label={t('general_status_label')}
                  name="statusType"
                  onChange={onComboChange('statusType')}
                  value={watch('statusType')}
                />
              </FormElement>
              <FormElement>
                <Combobox
                  errorMessage={t(errors.closureType?.message as string)}
                  items={closureTypePRM}
                  label={t('problemTrackingSystem_typeOfClosure_label')}
                  name="closureType"
                  onChange={onComboChange('closureType')}
                  value={watch('closureType')}
                  disabled
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <TextAreaContainer>
                <TextArea
                  errorMessage={t(errors.recordDetail?.message as string)}
                  label={`${t('problemTrackingSystem_recordDetail_label')} *`}
                  maxLength={TEXT_AREA_MAX_LENGTH}
                  onChange={handleTextAreaChange}
                  value={watch('recordDetail')}
                />
              </TextAreaContainer>
            </FormRow>
            <FormRow>
              <FormElement>
                <Title>
                  <Trans i18nKey="general_components_uploadedDocuments_text" />
                </Title>
              </FormElement>
            </FormRow>
            <FormRow>
              <UploadTable
                customerId={customerSearchValues.customerId}
                documentTableHeaders={[
                  t('general_components_documentNumber_label'),
                  t('general_components_comment_label'),
                ]}
                setDocumentList={setDocumentList}
                widths={['30', '70']}
              />
            </FormRow>
            <FormRow>
              <Button
                color={ButtonColor.Primary}
                size={ButtonSize.Medium}
                text={t('general_save_button')}
                type="submit"
              />
            </FormRow>
          </>
        ) : null}
      </Form>
      {isApplicationCompleted ? (
        <Modal
          description={t('problemTrackingSystem_successMessageNew_text')}
          onCancel={handleApplicationSuccessClick}
          submitText={t('general_components_close_button')}
          title={t('general_wellDone_text')}
          type={ModalType.Success}
        />
      ) : null}
      {applicationSubmitError ? (
        <Modal
          description={applicationSubmitError}
          onCancel={handleApplicationErrorClick}
          submitText={t('general_components_ok_button')}
          title={t('general_warning_text')}
          type={ModalType.Error}
        />
      ) : null}
    </>
  );
};

export default NewRecord;
